import React, { useRef } from 'react';
import { toast } from 'react-toastify';

import { auth0Audience, Auth0Metadata } from '../../api/API';
import { CourseGroupV2, CourseType } from '../../api/models/Course';
import { Environment } from '../../Environment';
import { history } from '../../util/History';
import { Auth0Context, useAuth0 } from '../Auth0Provider';

export type OverViewProps = {
  courseGroups: CourseGroupV2[];
  isGettingCourseGroups: boolean;
  userPressedCourseDetails: (courseType: CourseType) => void;
};

export const academicOverviewContainer = (OverViewScreen: React.ComponentType<OverViewProps>) => () => {
  const [courseGroups, setCourseGroups] = React.useState<CourseGroupV2[]>([]);
  const [isGettingCourseGroups, setIsGettingCourseGroups] = React.useState(true);

  const { api } = Environment.current();
  const { isAuthenticated, getTokenSilently, user } = useAuth0() as Auth0Context;

  const personId = user?.[Auth0Metadata.PersonId];
  const isMounted = useRef(true);

  // To prevent updates on unmount
  React.useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  React.useEffect(() => {
    (async () => {
      if (isAuthenticated && personId) {
        try {
          const token = await getTokenSilently({ audience: auth0Audience });
          const result = await api.getStudentCourseGroups(personId, token || '');
          if (isMounted.current) {
            setIsGettingCourseGroups(false);
            setCourseGroups(result);
          }
        } catch (e) {
          toast.error(`${e.message} (Academic Overview)`);
          setIsGettingCourseGroups(false);
        }
      }
    })();
  }, [isAuthenticated, personId]);

  const _userPressedCourseDetails = (courseType: CourseType) => {
    history.push(`/academic-overview/${courseType.toLowerCase()}`);
  };

  return (
    <OverViewScreen
      courseGroups={courseGroups}
      isGettingCourseGroups={isGettingCourseGroups}
      userPressedCourseDetails={_userPressedCourseDetails}
    />
  );
};
